import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import {
  Card,
  Crumbs,
  DateRangePopover,
  Gauge,
  KpiTile,
  LineChart,
  PageHead,
} from "components/AtomsV2";
import { Icon } from "components/ShellV2";

import { selectTimeSelector, updateTimeSelector } from "state/application/applicationSlice";
import {
  useGetCommunityKpisByIdQuery,
  useGetMembersKpisByIdQuery,
  useGetCommunityACEnergyByIdQuery,
  useGetCommunitySavingsByIdQuery,
} from "state/communities/communitiesApiSlice";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { selectAllMembers, useGetMembersByCommunityIdQuery } from "state/members/membersApiSlice";

import { aggregateBy } from "utils/data";
import { formatDate } from "utils/time";

import RankingList from "./components/RankingList";

// The dashboard always shows daily trends, regardless of the selected range.
const DASHBOARD_GRANULARITY = "DAY";

function format(value, fractionDigits = 2) {
  if (value == null || value === 0) return "—";
  return Number(value).toLocaleString(undefined, { maximumFractionDigits: fractionDigits });
}

function toNumericSeries(rows, field) {
  if (!Array.isArray(rows)) return [];
  const out = [];
  rows.forEach((r) => {
    const v = r?.[field];
    if (v != null && !Number.isNaN(Number(v))) out.push(Number(v));
  });
  return out;
}

// Dashboard chart x-axis labels use a compact numeric date (dd/MM/yy).
// Parse leniently (new Date handles ISO, "YYYY-MM-DD HH:mm:ss", epoch…) so the
// labels never collapse to empty (which would make ApexCharts show 1..N).
const pad2 = (n) => String(n).padStart(2, "0");
function toTimeLabels(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map((r) => {
    const ts = r?.datetime_timestamp;
    if (!ts) return "";
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return "";
    return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${String(d.getFullYear()).slice(-2)}`;
  });
}

export default function Dashboard() {
  const { t } = useTranslation(["dashboard", "common"]);
  const community = useSelector(selectCurrentCommunity);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { startDate, endDate, horizon, isExpanded } = useSelector(selectTimeSelector);

  const queryArgs = { communityId: community?.id, startDate, endDate };
  const queryArgsWithGran = { ...queryArgs, granularity: DASHBOARD_GRANULARITY };
  const fetchOpts = { refetchOnFocus: true, refetchOnMountOrArgChange: true };

  const { data: communityKpis } = useGetCommunityKpisByIdQuery(queryArgs, fetchOpts);
  const { data: membersKpis } = useGetMembersKpisByIdQuery(queryArgs, fetchOpts);
  const { data: savingsRaw } = useGetCommunitySavingsByIdQuery(queryArgsWithGran, fetchOpts);
  const { data: acEnergyRaw } = useGetCommunityACEnergyByIdQuery(queryArgsWithGran, fetchOpts);

  const { members } = useGetMembersByCommunityIdQuery(
    { communityId: community?.id, excludeDeleted: "true" },
    {
      selectFromResult: ({ data: membersData }) => ({
        members: membersData ? selectAllMembers(membersData) : [],
      }),
    }
  );

  const savingsSeries = useMemo(() => toNumericSeries(savingsRaw, "savings"), [savingsRaw]);
  const savingsLabels = useMemo(() => toTimeLabels(savingsRaw), [savingsRaw]);

  const acEnergyAggregated = useMemo(
    () => aggregateBy(acEnergyRaw, "datetime_timestamp") || [],
    [acEnergyRaw]
  );
  const acEnergySeries = useMemo(
    () => toNumericSeries(acEnergyAggregated, "energy"),
    [acEnergyAggregated]
  );
  const acEnergyLabels = useMemo(() => toTimeLabels(acEnergyAggregated), [acEnergyAggregated]);

  // Join membersKpis (dict keyed by member_id) with the members list to get names.
  // Mirrors the legacy Ranking page join pattern.
  const rankingMembers = useMemo(() => {
    if (!membersKpis) return [];
    return Object.entries(membersKpis).map(([memberId, metrics]) => {
      const meta = members?.find((m) => m.id === memberId) || {};
      return {
        id: memberId,
        member_id: memberId,
        name: meta.name || memberId,
        ...metrics,
      };
    });
  }, [membersKpis, members]);

  const kpis = communityKpis || {};
  const lastSessionLabel = useMemo(() => {
    if (!kpis.last_session) return "—";
    const d = new Date(kpis.last_session);
    if (Number.isNaN(d.getTime())) return "—";
    return formatDate(d);
  }, [kpis.last_session]);

  return (
    <div>
      <Crumbs
        items={[
          { label: community?.name || "—" },
          { label: t("page.title", { defaultValue: "Dashboard" }) },
        ]}
      />

      <PageHead
        title={t("page.title", { defaultValue: "Dashboard" })}
        subtitle={t("page.subtitle", {
          defaultValue: "Visão geral do desempenho energético da comunidade",
        })}
        actions={
          <DateRangePopover
            value={{ start: startDate, end: endDate }}
            onChange={(v) =>
              dispatch(
                updateTimeSelector({
                  startDate: v.start,
                  endDate: v.end,
                  horizon,
                  isExpanded,
                })
              )
            }
          />
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-rec mb-rec">
        <KpiTile
          icon="savings"
          tint="teal"
          label={t("kpi.savings", { defaultValue: "Poupança" })}
          value={format(kpis.savings)}
          unit="€"
          sparklineData={savingsSeries.length > 1 ? savingsSeries : null}
          sparklineColor="var(--accent)"
        />
        <KpiTile
          icon="eco"
          tint="green"
          label={t("kpi.selfSufficiency", { defaultValue: "Autossuficiência" })}
          value={format(kpis.self_sufficiency)}
          unit="%"
        />
        <KpiTile
          icon="bolt"
          tint="amber"
          label={t("kpi.acEnergy", { defaultValue: "Autoconsumo" })}
          value={format(kpis.injected_ac_energy)}
          unit="%"
          sparklineData={acEnergySeries.length > 1 ? acEnergySeries : null}
          sparklineColor="var(--warm)"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-rec mb-rec">
        <Card className="lg:col-span-2">
          <div className="flex items-start justify-between mb-2">
            <div>
              <div className="text-[15px] font-bold text-ink">
                {t("chart.savingsTitle", { defaultValue: "Poupança" })}
              </div>
              <div className="text-[12px] text-ink-3 mt-[2px]">
                {t("chart.savingsSubtitle", {
                  defaultValue: "Euros por dia · período selecionado",
                })}
              </div>
            </div>
          </div>
          <LineChart
            series={[
              {
                name: t("chart.savingsTitle", { defaultValue: "Poupança" }),
                data: savingsSeries,
                color: "var(--accent)",
              },
            ]}
            labels={savingsLabels}
            unit="€"
            height={320}
            type="area"
            xTitle={t("charts.axis_date", { ns: "common", defaultValue: "Data" })}
          />
        </Card>

        <Card>
          <RankingList
            members={rankingMembers}
            scoreField="savings"
            unit="€"
            limit={4}
            memberLinkBase={community?.id ? `/communities/${community.id}/members` : null}
            footerLink={community?.id ? `/communities/${community.id}/members` : null}
          />
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-rec">
        <Card className="lg:col-span-2">
          <div className="flex items-start justify-between mb-2">
            <div>
              <div className="text-[15px] font-bold text-ink">
                {t("chart.acTitle", { defaultValue: "Energia autoconsumida" })}
              </div>
              <div className="text-[12px] text-ink-3 mt-[2px]">
                {t("chart.acSubtitle", {
                  defaultValue: "kWh · autoconsumo vs. consumo da rede",
                })}
              </div>
            </div>
          </div>
          <LineChart
            series={[
              {
                name: t("chart.acSelfLabel", { defaultValue: "Autoconsumida" }),
                data: acEnergySeries,
                color: "var(--warm)",
              },
            ]}
            labels={acEnergyLabels}
            unit="kWh"
            height={320}
            type="area"
            xTitle={t("charts.axis_date", { ns: "common", defaultValue: "Data" })}
          />
        </Card>

        <div className="flex flex-col gap-rec">
          <Card className="flex-1">
            <div className="flex items-center gap-[18px] h-full">
              <Gauge
                value={Number(kpis.self_sufficiency) || 0}
                max={100}
                label={t("perf.gaugeLabel", { defaultValue: "Autossuficiência" })}
                color="var(--accent)"
                size={128}
              />
              <div className="flex-1 min-w-0">
                <div className="text-[15px] font-bold text-ink mb-2">
                  {t("perf.title", { defaultValue: "Desempenho energético" })}
                </div>
                <div
                  className="flex items-center justify-between py-[8px]"
                  style={{ borderBottom: "1px solid var(--border)" }}
                >
                  <span className="text-[13px] text-ink-2">
                    {t("perf.savings", { defaultValue: "Poupança" })}
                  </span>
                  <span className="text-[15px] font-bold text-ink tabular-nums">
                    {`${format(kpis.savings)} €`}
                  </span>
                </div>
                <div
                  className="flex items-center justify-between py-[8px]"
                  style={{ borderBottom: "1px solid var(--border)" }}
                >
                  <span className="text-[13px] text-ink-2">
                    {t("perf.injected", { defaultValue: "Energia injetada" })}
                  </span>
                  <span className="text-[15px] font-bold text-ink tabular-nums">
                    {`${format(kpis.injected_energy)} kWh`}
                  </span>
                </div>
                <div className="flex items-center justify-between py-[8px]">
                  <span className="text-[13px] text-ink-2">
                    {t("perf.lastSession", { defaultValue: "Última sessão" })}
                  </span>
                  <span className="text-[12.5px] font-bold text-ink">{lastSessionLabel}</span>
                </div>
              </div>
            </div>
          </Card>
          <Card className="flex-1">
            <div className="text-[15px] font-bold text-ink mb-2 flex items-center gap-2">
              <Icon name="apartment" fill style={{ color: "var(--accent)" }} />
              {t("infra.title", { defaultValue: "Infraestrutura comunitária" })}
            </div>
            <div className="flex flex-col">
              {[
                {
                  key: "members",
                  icon: "groups",
                  tint: "rec-tint-teal",
                  label: t("infra.members", { defaultValue: "Membros" }),
                  sub: t("infra.members_sub", { defaultValue: "membros ativos na CER" }),
                  value: community?.total_members ?? kpis.total_members ?? 0,
                  route: community?.id ? `/communities/${community.id}/members` : null,
                },
                {
                  key: "resources",
                  icon: "solar_power",
                  tint: "rec-tint-amber",
                  label: t("infra.resources", { defaultValue: "Recursos" }),
                  sub: t("infra.resources_sub", { defaultValue: "UPAC, baterias, EV…" }),
                  value: kpis.total_resources ?? 0,
                  route: community?.id
                    ? `/communities/${community.id}/infrastructure/resources`
                    : null,
                },
                {
                  key: "meters",
                  icon: "electric_meter",
                  tint: "rec-tint-green",
                  label: t("infra.meters", { defaultValue: "Contadores" }),
                  sub: t("infra.meters_sub", { defaultValue: "CPE e internos" }),
                  value: kpis.total_meters ?? 0,
                  route: community?.id
                    ? `/communities/${community.id}/infrastructure/meters`
                    : null,
                },
              ].map((item, i, arr) => (
                <button
                  key={item.key}
                  type="button"
                  disabled={!item.route}
                  onClick={() => item.route && navigate(item.route)}
                  className="flex items-center gap-[14px] w-full text-left bg-transparent border-0 py-[14px] cursor-pointer disabled:cursor-default group"
                  style={
                    i < arr.length - 1 ? { borderBottom: "1px solid var(--border)" } : undefined
                  }
                >
                  <div className={`rec-kpi-ic ${item.tint}`}>
                    <Icon name={item.icon} fill />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[15px] font-bold text-ink leading-tight">{item.label}</div>
                    <div className="text-[12.5px] text-ink-3 truncate">{item.sub}</div>
                  </div>
                  <div className="text-[26px] font-bold text-ink tabular-nums tracking-[-0.5px]">
                    {item.value}
                  </div>
                  <Icon
                    name="chevron_right"
                    style={{ color: "var(--ink-3)", fontSize: 20 }}
                    className="group-hover:text-ink transition-colors"
                  />
                </button>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
